/**
 * Schema Version Normalizer
 * 
 * CRITICAL: This module ensures backward compatibility for form data.
 * Old notes saved months/years ago MUST render correctly with new code.
 * 
 * HOW IT WORKS:
 * 1. Detects the schema version of incoming data
 * 2. Transforms legacy formats to the current schema
 * 3. Provides fallbacks for missing fields
 * 
 * SCHEMA HISTORY:
 * - v1 (Legacy): Fields stored as flat key-value pairs in note.content
 * - v2 (Template-based): Fields stored with template_id and form_data object
 * - v3 (Current): Enhanced with metadata, linked_goals, consent data
 * 
 * ADDING NEW VERSIONS:
 * 1. Document the changes in SCHEMA HISTORY
 * 2. Add a normalizer function (normalizeFromVX)
 * 3. Update detectSchemaVersion()
 * 4. Add transformation in normalizeNoteData()
 */

/**
 * Detect schema version from note data
 * @param {Object} noteData - Raw note data
 * @returns {number} Schema version (1, 2, or 3)
 */
export const detectSchemaVersion = (noteData) => {
  if (!noteData) return 3; // Default to current
  
  // V3: Has schema_version field explicitly set
  if (noteData.schema_version === 3 || noteData.schema_version === '3') {
    return 3;
  }
  
  // V3: Has newer fields like linked_goals, telehealth_consent, doc_number
  if (noteData.linked_goals || noteData.telehealth_consent || noteData.doc_number) {
    return 3;
  }
  
  // V2: Has template_id and structured form_data
  if (noteData.template_id && (noteData.form_data || noteData.fields)) {
    return 2;
  }
  
  // V2: Has template_fields array (form builder structure)
  if (noteData.template_fields && Array.isArray(noteData.template_fields)) {
    return 2;
  }
  
  // V1: Legacy flat structure or plain text content
  return 1;
};

/**
 * Normalize V1 (Legacy) data to current schema
 * V1 notes might have:
 * - Plain text content
 * - Flat key-value field data
 * - No template reference
 */
const normalizeFromV1 = (noteData, templateData) => {
  // If it's just a string (very old plain text note)
  if (typeof noteData === 'string') {
    return {
      schema_version: 3,
      form_data: {
        content: noteData
      },
      template_id: null,
      template_fields: [],
      is_legacy: true,
      legacy_format: 'plain_text'
    };
  }
  
  // V1 flat structure - convert to form_data
  const formData = {};
  const knownMetaFields = ['id', 'client_id', 'provider_id', 'created_at', 'updated_at', 
    'status', 'title', 'note_type', 'appointment_id', 'is_locked', 'locked_at', 'locked_by'];
  
  Object.entries(noteData).forEach(([key, value]) => {
    if (!knownMetaFields.includes(key)) {
      formData[key] = value;
    }
  });
  
  // Try to infer template structure from the data
  const inferredFields = Object.keys(formData).map((key, index) => ({
    id: key,
    label: formatLegacyFieldLabel(key),
    type: inferFieldType(formData[key]),
    order: index
  }));
  
  return {
    schema_version: 3,
    form_data: formData,
    template_id: noteData.template_id || null,
    template_fields: inferredFields,
    is_legacy: true,
    legacy_format: 'v1_flat'
  };
};

/**
 * Normalize V2 (Template-based) data to current schema
 * V2 notes have:
 * - template_id reference
 * - form_data or fields object
 * - template_fields array
 */
const normalizeFromV2 = (noteData, templateData) => {
  // V2 should have form_data, but might use "fields" instead
  const formData = noteData.form_data || noteData.fields || {};
  
  // Ensure template_fields exists
  let templateFields = noteData.template_fields || [];
  
  // If no template_fields but we have templateData, use that
  if (templateFields.length === 0 && templateData?.fields) {
    templateFields = templateData.fields;
  }
  
  // Handle matrix field format changes
  const normalizedFormData = normalizeMatrixFields(formData, templateFields);
  
  return {
    schema_version: 3,
    form_data: normalizedFormData,
    template_id: noteData.template_id,
    template_fields: templateFields,
    linked_goals: noteData.linked_goals || [],
    telehealth_consent: noteData.telehealth_consent || null,
    is_legacy: true,
    legacy_format: 'v2_template'
  };
};

/**
 * Normalize V3 (Current) data - minimal changes needed
 */
const normalizeFromV3 = (noteData, templateData) => {
  // V3 is current, but ensure all expected fields exist
  return {
    schema_version: 3,
    form_data: noteData.form_data || noteData.fields || {},
    template_id: noteData.template_id,
    template_fields: noteData.template_fields || templateData?.fields || [],
    linked_goals: noteData.linked_goals || [],
    telehealth_consent: noteData.telehealth_consent || null,
    doc_number: noteData.doc_number || null,
    is_legacy: false,
    legacy_format: null
  };
};

/**
 * Normalize matrix field data for backward compatibility
 * Matrix format has changed over time:
 * - Old: { "0-0": "value", "0-1": "value" }
 * - Old: { rows: [{...}] }
 * - Current: { "rowIndex-colIndex": "value", _extraRows: [...] }
 */
const normalizeMatrixFields = (formData, templateFields) => {
  const matrixFields = templateFields.filter(f => f.type === 'matrix');
  const normalized = { ...formData };
  
  matrixFields.forEach(field => {
    const fieldData = formData[field.id];
    if (!fieldData) return;
    
    // Already in current format
    if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
      // Check for old "rows" array format
      if (fieldData.rows && Array.isArray(fieldData.rows)) {
        // Convert rows array to current format
        const converted = {};
        const extraRows = [];
        
        fieldData.rows.forEach((row, rowIndex) => {
          if (row.label && rowIndex >= (field.rowLabels?.length || 0)) {
            extraRows.push(row.label);
          }
          if (row.values) {
            Object.entries(row.values).forEach(([colIndex, value]) => {
              converted[`${rowIndex}-${colIndex}`] = value;
            });
          }
        });
        
        if (extraRows.length > 0) {
          converted._extraRows = extraRows;
        }
        
        normalized[field.id] = converted;
      }
      // Current format - keep as is but ensure _extraRows exists
      else if (!fieldData._extraRows) {
        normalized[field.id] = { ...fieldData, _extraRows: [] };
      }
    }
  });
  
  return normalized;
};

/**
 * Convert legacy field key to readable label
 * e.g., "client_mood_rating" -> "Client Mood Rating"
 */
const formatLegacyFieldLabel = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Infer field type from value
 */
const inferFieldType = (value) => {
  if (value === null || value === undefined) return 'text';
  if (typeof value === 'boolean') return 'checkbox';
  if (Array.isArray(value)) return 'checkbox_group';
  if (typeof value === 'object') return 'matrix';
  if (typeof value === 'string') {
    if (value.startsWith('data:image')) return 'signature';
    if (value.length > 200) return 'textarea';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
  }
  return 'text';
};

/**
 * MAIN FUNCTION: Normalize note data to current schema
 * 
 * @param {Object|string} noteContent - Raw note content (may be JSON string)
 * @param {Object} templateData - Optional template data for field structure
 * @returns {Object} Normalized note data in V3 format
 */
export const normalizeNoteData = (noteContent, templateData = null) => {
  // Handle null/undefined
  if (!noteContent) {
    return {
      schema_version: 3,
      form_data: {},
      template_id: null,
      template_fields: templateData?.fields || [],
      linked_goals: [],
      telehealth_consent: null,
      is_legacy: false,
      legacy_format: null
    };
  }
  
  // Parse JSON string if needed
  let noteData = noteContent;
  if (typeof noteContent === 'string') {
    try {
      noteData = JSON.parse(noteContent);
    } catch (e) {
      // Plain text content
      return normalizeFromV1(noteContent, templateData);
    }
  }
  
  // Detect version and normalize
  const version = detectSchemaVersion(noteData);
  
  switch (version) {
    case 1:
      return normalizeFromV1(noteData, templateData);
    case 2:
      return normalizeFromV2(noteData, templateData);
    case 3:
    default:
      return normalizeFromV3(noteData, templateData);
  }
};

/**
 * Merge template data with saved note data
 * Ensures all template fields exist even if not saved in note
 * 
 * @param {Object} normalizedData - Output from normalizeNoteData()
 * @param {Object} templateData - Current template definition
 * @returns {Object} Merged data with all fields present
 */
export const mergeWithTemplate = (normalizedData, templateData) => {
  if (!templateData?.fields) return normalizedData;
  
  const mergedFormData = { ...normalizedData.form_data };
  
  // Ensure every template field has a value (even if empty)
  templateData.fields.forEach(field => {
    if (!(field.id in mergedFormData)) {
      // Set default value based on type
      switch (field.type) {
        case 'checkbox':
          mergedFormData[field.id] = false;
          break;
        case 'checkbox_group':
        case 'multiselect':
        case 'multi-select':
          mergedFormData[field.id] = [];
          break;
        case 'matrix':
          mergedFormData[field.id] = { _extraRows: [] };
          break;
        default:
          mergedFormData[field.id] = field.default_value || '';
      }
    }
  });
  
  return {
    ...normalizedData,
    form_data: mergedFormData,
    template_fields: templateData.fields
  };
};

/**
 * Validate normalized data has required fields
 * Returns array of validation errors
 */
export const validateNormalizedData = (normalizedData, templateData) => {
  const errors = [];
  
  if (!normalizedData.form_data) {
    errors.push({ type: 'missing', field: 'form_data', message: 'Form data is missing' });
    return errors;
  }
  
  // Check required fields
  const requiredFields = (templateData?.fields || normalizedData.template_fields || [])
    .filter(f => f.required);
  
  requiredFields.forEach(field => {
    const value = normalizedData.form_data[field.id];
    const isEmpty = value === undefined || value === null || value === '' ||
      (Array.isArray(value) && value.length === 0);
    
    if (isEmpty) {
      errors.push({
        type: 'required',
        field: field.id,
        label: field.label,
        message: `${field.label || field.id} is required`
      });
    }
  });
  
  return errors;
};

/**
 * Get a human-readable description of schema changes
 * Useful for debugging legacy data issues
 */
export const describeSchemaVersion = (version) => {
  const descriptions = {
    1: 'Legacy (flat key-value pairs or plain text)',
    2: 'Template-based (form_data with template_id)',
    3: 'Current (enhanced with linked_goals, consent, doc_number)'
  };
  return descriptions[version] || 'Unknown';
};

export default {
  detectSchemaVersion,
  normalizeNoteData,
  mergeWithTemplate,
  validateNormalizedData,
  describeSchemaVersion
};
